import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/landing";

const links = [
  { label: "Features", href: "#features" },
  { label: "How it works", href: "#feat-home" },
  { label: "FAQ", href: "#faq", event: "growcalc:faq" },
  { label: "Tools", href: "#faq", event: "growcalc:tools" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="landing-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[var(--ld-bg)]/85 backdrop-blur-xl border-b border-[var(--ld-line)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <a href="#top" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img src={LOGO_URL} alt="GrowCalc" className="h-9 w-9 rounded-xl object-contain" />
          <span className="flex flex-col leading-none">
            <span className="text-lg font-black tracking-tight">GrowCalc</span>
            <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--ld-lime)]">
              <Leaf className="h-2.5 w-2.5" /> All-in-One Lawncare Tool
            </span>
          </span>
        </a>

        <div className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => l.event && window.dispatchEvent(new Event(l.event))}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-bold text-[var(--ld-muted)] transition-colors hover:text-[var(--ld-text)]"
            >
              {l.label}
            </a>
          ))}
        </div>

        <a
          href="#get"
          data-testid="nav-cta"
          className="ld-btn-lime hidden rounded-full px-5 py-2.5 text-sm font-black md:inline-flex"
        >
          Get the App
        </a>

        <button
          className="text-[var(--ld-text)] md:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-[var(--ld-line)] bg-[var(--ld-bg)]/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => {
                    setOpen(false);
                    l.event && window.dispatchEvent(new Event(l.event));
                  }}
                  className="rounded-lg px-3 py-3 text-base font-bold text-[var(--ld-text)] hover:bg-[var(--ld-surface)]"
                >
                  {l.label}
                </a>
              ))}
              <a href="#get" onClick={() => setOpen(false)} className="ld-btn-lime mt-2 rounded-full px-5 py-3 text-center text-sm font-black">
                Get the App
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
